import React from "react";
import { Button, Form, FormGroup, Spinner, Row, Col } from "react-bootstrap";
import { format, isValid, parseISO } from "date-fns";

import { updateLease } from "../../../../../api/RealEstate";

export default function LeaseForm(props: {
  bagObject: IBagObject;
}): React.JSX.Element {
  /**
   * A form to create/update a lease.
   */
  const [bagObject, setBagObject] = React.useState<IBagObject>(props.bagObject);

  const [leaseOwnerName, setLeaseOwnerName] = React.useState<string>(
    bagObject.lease?.ownerName || "",
  );
  const [leaseOwnerKvk, setLeaseOwnerKvk] = React.useState<string>(
    bagObject.lease?.ownerKvk || "",
  );
  const [leaseTenantName, setLeaseTenantName] = React.useState<string>(
    bagObject.lease?.tenantName || "",
  );
  const [leaseTenantKvk, setLeaseTenantKvk] = React.useState<string>(
    bagObject.lease?.tenantKvk || "",
  );
  const [leaseBrokerName, setLeaseBrokerName] = React.useState<string>(
    bagObject.lease?.brokerName || "",
  );
  const [leaseBrokerKvk, setLeaseBrokerKvk] = React.useState<string>(
    bagObject.lease?.brokerKvk || "",
  );
  const [leaseIncentives, setLeaseIncentives] = React.useState<string>(
    bagObject.lease?.incentives || "",
  );
  const [leaseNotes, setLeaseNotes] = React.useState<string>(
    bagObject.lease?.notes || "",
  );
  const [leasePricePerM2PerYear, setLeasePricePerM2PerYear] = React.useState<
    number | null
  >(bagObject.lease?.pricePerM2PerYear || null);
  const [leaseStartDate, setLeaseStartDate] = React.useState<Date | null>(
    bagObject.lease?.startDate || null,
  );
  const [leaseEndDate, setLeaseEndDate] = React.useState<Date | null>(
    bagObject.lease?.endDate || null,
  );

  const [errorMessage, setErrorMessage] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  interface IDatalistOrganization {
    name: string;
    kvkNumber: string | null;
  }

  const datalistOrganizations = React.useMemo<IDatalistOrganization[]>(() => {
    /**
     * Get organization names and KVK numbers, for populating the datalist options.
     * If multiple organizations have the same name, prefer the one with a KVK number.
     */
    const organizationBasicDetails: IDatalistOrganization[] = [];

    bagObject.organizations.forEach((_organization) => {
      const existingDatalistOrganization = organizationBasicDetails.find(
        (_datalistOrganization) =>
          _datalistOrganization.name === _organization.name,
      );

      // Get KVK number from bedrijfsdata, if available.
      let kvkNumber: string | null = null;
      const bedrijfsdata = _organization.bedrijfsdata.find(
        (_bedrijfsdata) => _bedrijfsdata.kvkNumber,
      );
      if (bedrijfsdata && bedrijfsdata.kvkNumber) {
        kvkNumber = bedrijfsdata.kvkNumber;
      }

      // Only add if not already in the list or if this one has a KVK number and the existing one doesn't.
      if (
        !existingDatalistOrganization ||
        (!existingDatalistOrganization.kvkNumber && kvkNumber)
      ) {
        organizationBasicDetails.push({
          name: _organization.name,
          kvkNumber: kvkNumber,
        });
      }
    });

    return organizationBasicDetails;
  }, [bagObject.organizations]);

  const handleOwnerNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    /**
     * Handle changes to the owner name input field.
     */
    const updatedOwnerName = e.target.value;
    setLeaseOwnerName(updatedOwnerName);
    // Fill in KVK if organization with this name exists and has KVK.
    const organization = datalistOrganizations.find(
      (_organization) => _organization.name === updatedOwnerName,
    );
    if (organization && organization.kvkNumber) {
      setLeaseOwnerKvk(organization.kvkNumber);
    }
  };

  const handleTenantNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    /**
     * Handle changes to the tenant name input field.
     */
    const updatedTenantName = e.target.value;
    setLeaseTenantName(updatedTenantName);
    // Fill in KVK if organization with this name exists and has KVK.
    const organization = datalistOrganizations.find(
      (_organization) => _organization.name === updatedTenantName,
    );
    if (organization && organization.kvkNumber) {
      setLeaseTenantKvk(organization.kvkNumber);
    }
  };

  const isFormComplete = (): boolean => {
    /**
     * Check that at least one field value has changed.
     */
    return (
      leaseOwnerName !== (bagObject.lease?.ownerName || "") ||
      leaseOwnerKvk !== (bagObject.lease?.ownerKvk || "") ||
      leaseTenantName !== (bagObject.lease?.tenantName || "") ||
      leaseTenantKvk !== (bagObject.lease?.tenantKvk || "") ||
      leaseBrokerName !== (bagObject.lease?.brokerName || "") ||
      leaseBrokerKvk !== (bagObject.lease?.brokerKvk || "") ||
      leaseIncentives !== (bagObject.lease?.incentives || "") ||
      leaseNotes !== (bagObject.lease?.notes || "") ||
      leasePricePerM2PerYear !== (bagObject.lease?.pricePerM2PerYear || null) ||
      leaseStartDate !== (bagObject.lease?.startDate || null) ||
      leaseEndDate !== (bagObject.lease?.endDate || null)
    );
  };

  const handleSubmitLease = async (e: React.FormEvent) => {
    /**
     * Handle the form submission to update the BAG object's app data.
     */
    e.preventDefault();
    setIsLoading(true);
    // Clear any previous messages.
    setErrorMessage("");
    setSuccessMessage("");
    const lease: ILease = {
      ownerName: leaseOwnerName,
      ownerKvk: leaseOwnerKvk,
      tenantName: leaseTenantName,
      tenantKvk: leaseTenantKvk,
      brokerName: leaseBrokerName,
      brokerKvk: leaseBrokerKvk,
      incentives: leaseIncentives,
      notes: leaseNotes,
      pricePerM2PerYear: leasePricePerM2PerYear,
      startDate: leaseStartDate,
      endDate: leaseEndDate,
    };
    await updateLease(bagObject.id, lease)
      .then(() => {
        setBagObject({ ...bagObject, lease: lease });
        setSuccessMessage("Aanpassingen zijn succesvol opgeslagen.");
      })
      .catch((error: IApiError) => setErrorMessage(error.message));
    setIsLoading(false);
  };

  return (
    <Form
      className="d-flex flex-column flex-grow-1 justify-content-between align-items-center"
      onSubmit={handleSubmitLease}
    >
      <div className="w-100">
        {/* Owner fields. */}
        <Row className="mb-3">
          <Col md={6}>
            <FormGroup>
              <Form.Label htmlFor={`owner_name_${bagObject.id}`}>
                <strong>Eigenaar naam</strong>
              </Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={leaseOwnerName}
                onChange={handleOwnerNameChange}
                id={`owner_name_${bagObject.id}`}
                list={`owner-names-${bagObject.id}`}
              />
              <datalist id={`owner-names-${bagObject.id}`}>
                {datalistOrganizations.map((org, index) => (
                  <option key={`owner-${index}`} value={org.name} />
                ))}
              </datalist>
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Form.Label htmlFor={`owner_kvk_${bagObject.id}`}>
                <strong>Eigenaar KVK</strong>
              </Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={leaseOwnerKvk}
                onChange={(e) => {
                  setLeaseOwnerKvk(e.target.value);
                  setSuccessMessage("");
                }}
                id={`owner_kvk_${bagObject.id}`}
              />
            </FormGroup>
          </Col>
        </Row>

        {/* Tenant fields. */}
        <Row className="mb-3">
          <Col md={6}>
            <FormGroup>
              <Form.Label htmlFor={`tenant_name_${bagObject.id}`}>
                <strong>Huurder naam</strong>
              </Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={leaseTenantName}
                onChange={handleTenantNameChange}
                id={`tenant_name_${bagObject.id}`}
                list={`tenant-names-${bagObject.id}`}
              />
              <datalist id={`tenant-names-${bagObject.id}`}>
                {datalistOrganizations.map((org, index) => (
                  <option key={`tenant-${index}`} value={org.name} />
                ))}
              </datalist>
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Form.Label htmlFor={`tenant_kvk_${bagObject.id}`}>
                <strong>Huurder KVK</strong>
              </Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={leaseTenantKvk}
                onChange={(e) => {
                  setLeaseTenantKvk(e.target.value);
                  setSuccessMessage("");
                }}
                id={`tenant_kvk_${bagObject.id}`}
              />
            </FormGroup>
          </Col>
        </Row>

        {/* Broker fields. */}
        <Row className="mb-3">
          <Col md={6}>
            <FormGroup>
              <Form.Label htmlFor="broker_name">
                <strong>Makelaar naam</strong>
              </Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={leaseBrokerName}
                onChange={(e) => {
                  setLeaseBrokerName(e.target.value);
                  setSuccessMessage("");
                }}
                id="broker_name"
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Form.Label htmlFor="broker_kvk">
                <strong>Makelaar KVK</strong>
              </Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={leaseBrokerKvk}
                onChange={(e) => {
                  setLeaseBrokerKvk(e.target.value);
                  setSuccessMessage("");
                }}
                id="broker_kvk"
              />
            </FormGroup>
          </Col>
        </Row>

        {/* Incentives and notes fields. */}
        <Row className="mb-3">
          <Col md={6}>
            <FormGroup>
              <Form.Label htmlFor="incentives">
                <strong>Incentives</strong>
              </Form.Label>
              <Form.Control
                size="sm"
                className="rounded-0"
                type="text"
                as="textarea"
                rows={2}
                value={leaseIncentives}
                onChange={(e) => {
                  setLeaseIncentives(e.target.value);
                  setSuccessMessage("");
                }}
                id="incentives"
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Form.Label htmlFor="notes">
                <strong>Notities</strong>
              </Form.Label>
              <Form.Control
                size="sm"
                className="rounded-0"
                type="text"
                as="textarea"
                rows={2}
                value={leaseNotes}
                onChange={(e) => {
                  setLeaseNotes(e.target.value);
                  setSuccessMessage("");
                }}
                id="notes"
              />
            </FormGroup>
          </Col>
        </Row>

        {/* Price and date fields. */}
        <Row className="mb-3">
          <Col md={4}>
            <FormGroup>
              <Form.Label htmlFor="price_per_m2">
                <strong>Huurprijs (€/m²/jaar)</strong>
              </Form.Label>
              <Form.Control
                size="sm"
                type="number"
                step="0.01"
                value={
                  leasePricePerM2PerYear !== null ? leasePricePerM2PerYear : ""
                }
                onChange={(e) => {
                  setLeasePricePerM2PerYear(
                    e.target.value ? parseFloat(e.target.value) : null,
                  );
                  setSuccessMessage("");
                }}
                id="price_per_m2"
              />
            </FormGroup>
          </Col>

          <Col md={4}>
            <FormGroup>
              <Form.Label htmlFor="start_date">
                <strong>Begindatum</strong>
              </Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={
                  leaseStartDate && isValid(leaseStartDate)
                    ? format(leaseStartDate, "yyyy-MM-dd")
                    : ""
                }
                onChange={(e) => {
                  setLeaseStartDate(
                    e.target.value ? parseISO(e.target.value) : null,
                  );
                  setSuccessMessage("");
                }}
                id="start_date"
              />
            </FormGroup>
          </Col>

          <Col md={4}>
            <FormGroup>
              <Form.Label htmlFor="end_date">
                <strong>Einddatum</strong>
              </Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={
                  leaseEndDate && isValid(leaseEndDate)
                    ? format(leaseEndDate, "yyyy-MM-dd")
                    : ""
                }
                onChange={(e) => {
                  setLeaseEndDate(
                    e.target.value ? parseISO(e.target.value) : null,
                  );
                  setSuccessMessage("");
                }}
                id="end_date"
              />
            </FormGroup>
          </Col>
        </Row>

        <div className="d-flex w-100 align-items-center justify-content-between">
          {errorMessage ? (
            <Form.Text className="text-danger mb-1">{errorMessage}</Form.Text>
          ) : (
            <div>
              {successMessage && (
                <Form.Text className="text-success mb-1">
                  {successMessage}
                </Form.Text>
              )}
            </div>
          )}

          <Button
            size="sm"
            title="Opslaan"
            type="submit"
            variant="primary"
            className="px-4"
            disabled={isLoading || !isFormComplete()}
            style={{ minWidth: "7rem" }}
          >
            {isLoading ? (
              <Spinner animation="border" variant="dark" size="sm" />
            ) : (
              "Opslaan"
            )}
          </Button>
        </div>
      </div>
    </Form>
  );
}
